import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { MenuList } from '@mui/material';
import { NxObjectType } from '@warehouse/object-browser/core';
import { useUserScopes } from '@warehouse/auth';
import { objectBrowserStoreSelector, useObjectBrowserStore } from '@warehouse/object-browser/domain';
import { ConfirmationModal } from '@warehouse/shared/legacy-ui';
import Button from '../../../../src/components/library/Button';
import ModalMenu, { MenuListItem } from '../../../../src/components/library/ModalMenu';
import Tooltip from '../../../../src/components/library/Tooltip';
import TextInput from '../../../../src/components/library/TextInput';
import { BrokerService, NX_PURGE_SERVICE_UUID, matchesServiceRegexFilter } from '../core';
import { brokerServicesRepositorySingleton } from '../infra';
import { brokerJobsStoreSelector, useBrokerJobsStore } from '../domain/broker-jobs.store';
import { resolveServiceIcon } from './serviceIcon';

interface TriggerServiceMenuProps {
	nxObjectUuid: string;
	itemType: NxObjectType;
}

const TriggerButton = styled(Button)({
    minWidth: '36px',
    padding: 0,
    width: '36px',
});

const MenuSearchWrapper = styled('div')(({ theme }) => ({
    padding: `${theme.spacing(1.25)} ${theme.spacing(1.25)} ${theme.spacing(0.75)}`,
    width: '288px',
}));

const NoResults = styled('div')(({ theme }) => ({
	color: theme.palette.text.secondary,
    fontSize: theme.typography.bodySmallRegular.fontSize,
    padding: `${theme.spacing(1)} ${theme.spacing(1.75)} ${theme.spacing(1.5)}`,
}));

export function TriggerServiceMenu({ nxObjectUuid, itemType }: TriggerServiceMenuProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [serviceAwaitingConfirmation, setServiceAwaitingConfirmation] = useState<BrokerService | null>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const pendingServiceUuidsRef = useRef<Set<string>>(new Set());
	const { triggerService } = useBrokerJobsStore(brokerJobsStoreSelector.actions);
	const { enqueueSnackbar } = useSnackbar();
	const selectedNxObject = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObject);

	const availableServices = useMemo(() => brokerServicesRepositorySingleton.get().getAvailableServicesConfig(), []);
	const { scopes: userPermissions } = useUserScopes();
	
	const activeServices = useMemo(() =>
		availableServices.filter((service) => {
			if (!service.active) return false;
			if (itemType === NxObjectType.Folder) return service.inputType === 'folder';
			if (service.requiredPermissions && !service.requiredPermissions
				.every((permission) => userPermissions.includes(permission))) return false;
			return matchesServiceRegexFilter(service, selectedNxObject ?? {});
		}),
		[availableServices, userPermissions, itemType, selectedNxObject]
	);

	const filteredServices = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return activeServices;
		return activeServices.filter((service) => service.serviceName.toLowerCase().includes(query));
	}, [activeServices, search]);

	const close = () => {
		setOpen(false);
		setSearch('');
	};

	const runService = async (service: BrokerService) => {
		if (pendingServiceUuidsRef.current.has(service.serviceUUID)) return;
		pendingServiceUuidsRef.current.add(service.serviceUUID);

		try {
			const { jobId } = await triggerService(nxObjectUuid, service);
			if (jobId === 'error') {
				enqueueSnackbar(`Failed to trigger ${service.serviceName}`, {
					variant: 'error',
					preventDuplicate: true,
				});
			} else {
				enqueueSnackbar(`${service.serviceName} triggered successfully`, {
					variant: 'success',
					preventDuplicate: true,
				});
			}
		} finally {
			pendingServiceUuidsRef.current.delete(service.serviceUUID);
		}
	};

	const onConfirmService = () => {
		if (!serviceAwaitingConfirmation) return;
		runService(serviceAwaitingConfirmation);
		setServiceAwaitingConfirmation(null);
	};

	let menuContent: React.ReactNode;
	if (activeServices.length === 0) {
		menuContent = <NoResults>No services available for this object</NoResults>;
	} else if (filteredServices.length === 0) {
		menuContent = <NoResults>No services match &ldquo;{search}&rdquo;</NoResults>;
	} else {
		menuContent = (
			<MenuList>
				{filteredServices.map((service) => {
					const ServiceIcon = resolveServiceIcon(service.serviceIcon ?? '');
					return (
						<MenuListItem
							key={service.serviceUUID}
							icon={<ServiceIcon fontSize="small" />}
							text={service.serviceName}
							onClick={() => {
								close();
								if (service.serviceUUID === NX_PURGE_SERVICE_UUID) {
									setServiceAwaitingConfirmation(service);
									return;
								}
								runService(service);
							}}
						/>
					);
				})}
			</MenuList>
		);
	}

	return (
		<>
			<Tooltip title="Trigger a service">
				<TriggerButton
					ref={buttonRef}
					type="button"
					nxstyle="secondary-blue"
					aria-label="Trigger-Service-Button"
					onClick={(event) => {
						event.stopPropagation();
						setOpen((isOpen) => !isOpen);
					}}
				>
					<AddIcon />
				</TriggerButton>
			</Tooltip>
			<ModalMenu
				position="bottom-end"
				open={open}
				anchorEl={buttonRef.current}
				onClose={close}
				content={
					<div>
						<MenuSearchWrapper>
							<TextInput
								ariaLabel="Search-Trigger-Services"
								placeholder="Search services"
								startIcon={<SearchIcon />}
								height={32}
								disabledMargin
								fullWidth
								value={search}
								onChange={(event) => setSearch(event.target.value)}
							/>
						</MenuSearchWrapper>
						{ menuContent }
					</div>
				}
			/>
			<ConfirmationModal
				open={serviceAwaitingConfirmation !== null}
				onClose={() => setServiceAwaitingConfirmation(null)}
				onConfirm={onConfirmService}
				title="Delete Object"
				message={`This will permanently delete ${selectedNxObject?.name ?? 'this object'}. This action cannot be undone.`}
				confirmText="Delete"
				variant="danger"
				showButtonIcons={false}
			/>
		</>
	);
}
